import { Component, OnInit, signal } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CustomValidators } from 'angular-custom-validators';
import { CountryISO, NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { dataStore, db, initDataStoreOrgUser } from 'src/app/app.component';
import { OrgUser } from '../interfaces';
import { setDoc, doc } from 'firebase/firestore';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatNumericInput, logout } from '../globals';
import { debounceTime } from 'rxjs';
import { PostcodeApiService } from 'src/app/services/postcode-api.service';
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardContent,
  MatCardActions,
} from '@angular/material/card';
import {
  MatFormField,
  MatLabel,
  MatInput,
  MatError,
  MatSuffix,
  MatHint,
} from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatSelect, MatOption } from '@angular/material/select';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-first-time',
  templateUrl: './first-time.component.html',
  styleUrls: ['./first-time.component.scss'],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgxIntlTelInputModule,
    MatError,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatHint,
    NgClass,
    MatCardActions,
    MatButton,
    MatProgressSpinner,
  ],
})
export class FirstTimeComponent implements OnInit {
  dataStore = dataStore;
  formatNumericInput = formatNumericInput;
  loading = signal(true);
  personalForm: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    streetName: new UntypedFormControl('', [Validators.required]),
    postal: new UntypedFormControl('', [Validators.required, CustomValidators.postal]),
    houseNumber: new UntypedFormControl('', [
      Validators.required,
      CustomValidators.numberInput(false, false, 0),
      Validators.pattern(/^[0-9]*$/),
    ]),
    houseNumberAddition: new UntypedFormControl(''),
    city: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, CustomValidators.email]),
    phoneNumber: new UntypedFormControl('', [Validators.required]),
  });
  awaitingResponse = signal(false);
  showPasswords: boolean = false;
  CountryISO = CountryISO;
  windowRef: any;
  verificationCode: string;
  captcha: boolean;
  captchaIsRendering = false;
  verificationError = false;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    public postcodeApiService: PostcodeApiService,
  ) {}

  ngOnInit(): void {
    if (dataStore.orgUser.name) {
      this.router.navigate(['/profile']);
    }

    if (dataStore.publicSettings.askBirthDay) {
      this.personalForm.addControl('birthDate', new UntypedFormControl('', [Validators.required]));
    }
    if (dataStore.publicSettings.askGender) {
      this.personalForm.addControl('gender', new UntypedFormControl('', [Validators.required]));
    }

    this.applyContactValidators();

    this.personalForm.patchValue(dataStore.rootUser);
    if (this.personalForm.controls.email.value) {
      this.personalForm.controls.email.disable();
    }
    if (this.personalForm.controls.phoneNumber.value) {
      this.personalForm.controls.phoneNumber.disable();
    }
    this.personalForm.controls.postal.valueChanges.pipe(debounceTime(600)).subscribe(async () => {
      await this.getAddress();
    });
    this.personalForm.controls.houseNumber.valueChanges
      .pipe(debounceTime(600))
      .subscribe(async () => {
        await this.getAddress();
      });

    this.loading.set(false);
  }

  async logout() {
    await logout(this.router);
  }

  async getAddress() {
    if (
      this.personalForm.getRawValue().postal !== '' &&
      this.personalForm.controls.postal.valid &&
      this.personalForm.getRawValue().houseNumber !== '' &&
      this.personalForm.controls.houseNumber.valid
    ) {
      const addressSearchResult = await this.postcodeApiService.getAddress(
        this.personalForm.getRawValue().postal,
        this.personalForm.getRawValue().houseNumber,
      );
      if (addressSearchResult) {
        this.personalForm.controls.streetName.patchValue(addressSearchResult.street);
        this.personalForm.controls.city.patchValue(addressSearchResult.city);
      } else {
        this.personalForm.controls.streetName.reset(null);
        this.personalForm.controls.city.reset(null);
      }
    } else {
      this.personalForm.controls.streetName.reset(null);
      this.personalForm.controls.city.reset(null);
    }
  }

  async finishRegistration() {
    if (!this.personalForm.valid) {
      this.personalForm.markAllAsTouched();
      return;
    }

    this.awaitingResponse.set(true);
    try {
      const orgUserObj: OrgUser = this.personalForm.getRawValue();
      orgUserObj.phoneNumber = this.normalizePhoneNumberValue(
        this.personalForm.controls.phoneNumber.value,
      );
      orgUserObj.postal = orgUserObj.postal.toUpperCase().replace(/\s/g, '');

      await setDoc(
        doc(
          db,
          `organisations/${dataStore.selectedOrganisation.id}/users/${dataStore.rootUser.id}`,
        ),
        { ...orgUserObj, passenger: true },
        { merge: true },
      );
      await initDataStoreOrgUser();
      this.router.navigate(['/profile']);
    } catch (error) {
      console.log('Error: ', error);
      this.awaitingResponse.set(false);
      const errorCode =
        typeof error === 'object' && error !== null && 'code' in error
          ? (error as { code?: string }).code
          : undefined;
      switch (errorCode) {
        default:
          this.snackBar.open(
            'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
            'X',
          );
          break;
      }
    }
  }
  private applyContactValidators() {
    const signInMethod = dataStore.rootUser.signInMethod;
    const emailControl = this.personalForm.controls.email;
    const phoneNumberControl = this.personalForm.controls.phoneNumber;

    if (signInMethod === 'email') {
      emailControl.setValidators([Validators.required, CustomValidators.email]);
      phoneNumberControl.setValidators([]);
    } else if (signInMethod === 'phoneNumber') {
      emailControl.setValidators([CustomValidators.email]);
      phoneNumberControl.setValidators([Validators.required]);
    } else {
      emailControl.setValidators([CustomValidators.email]);
      phoneNumberControl.setValidators([]);
    }

    emailControl.updateValueAndValidity({ emitEvent: false });
    phoneNumberControl.updateValueAndValidity({ emitEvent: false });
  }

  private normalizePhoneNumberValue(phoneNumberValue: unknown): string {
    if (typeof phoneNumberValue === 'string') {
      return phoneNumberValue;
    }

    if (
      phoneNumberValue &&
      typeof phoneNumberValue === 'object' &&
      'e164Number' in phoneNumberValue &&
      typeof phoneNumberValue.e164Number === 'string'
    ) {
      return phoneNumberValue.e164Number;
    }

    return '';
  }
}
